import Vue from "vue";
import { Item } from "./item";

const RELATIONS = {
  plannedFor: ["day", "week", "month", "list"],
  subtaskOf: ["task", "list"],
  category: ["list"],
  status: ["list"],
};

export function defaultListID(couch) {
  return `list:${couch.name}`;
}

function docCmpForRelation(relation) {
  return (a, b) => {
    if (!a[relation]) {
      console.error(`Malformed object ${a._id}: no ${relation}`)
      return false
    }
    if (!b[relation]) {
      console.error(`Malformed object ${b._id}: no ${relation}`)
      return false
    }
    if (a[relation].seq === b[relation].seq) {
      return a._id < b._id;
    }
    return a[relation].seq < b[relation].seq;
  };
}

// Based on underscore's sortedIndex.
function indexInParentList(parentList, relation, obj) {
  const cmp = docCmpForRelation(relation);
  let low = 0;
  let high = parentList.length;
  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (cmp(parentList[mid], obj)) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return low;
}

function removeFromParentList(parentList, relation, obj) {
  const idx = indexInParentList(parentList, relation, obj);
  if (parentList[idx]._id == obj._id) {
    parentList.splice(idx, 1);
  }
}

function sortIntoParentList(parentList, relation, obj) {
  const idx = indexInParentList(parentList, relation, obj);
  parentList.splice(idx, 0, obj);
}

function emptyParentInfo(parentClass, parentKey) {
  return {
    parentClass,
    parentKey,
    children: [],
    activePendingChildrenCount: 0,
  };
}

function getParentInfo(cache, relation, { parentClass, parentKey }) {
  return (
    cache[relation][parentClass][parentKey] ||
    emptyParentInfo(parentClass, parentKey)
  );
}

export function getChildren({ cache, relation, pClass, pKey }) {
  const parentInfo = cache[relation][pClass][pKey];
  if (parentInfo) return parentInfo.children;
  else return [];
}

function getOrCreateParentInfo(cache, relation, { parentClass, parentKey }) {
  let classCache = cache[relation][parentClass];
  if (!classCache) {
    cache[relation][parentClass] = {};
    classCache = cache[relation][parentClass];
  }
  if (!classCache[parentKey]) {
    Vue.set(classCache, parentKey, emptyParentInfo(parentClass, parentKey));
  }
  return classCache[parentKey];
}

function activePending(doc) {
  return (
    !doc.archivedTimestampMs &&
    !doc.doneAtTimestampMs &&
    doc.type !== "note" &&
    doc.type !== "event" &&
    doc.type !== "separator"
  );
}

function modifyActivePendingCount(cache, relation, parentInfo, delta) {
  if (delta === 0) {
    return;
  }
  if (
    parentInfo.activePendingChildrenCount === 0 &&
    delta > 0 &&
    relation === "plannedFor" &&
    parentInfo.parentClass !== "list"
  ) {
    // Value becoming non-zero, we are a new candidate.
    const currentKey =
      cache.plannedFor[parentInfo.parentClass].$firstActivePendingKey;
    if (!currentKey || parentInfo.parentKey < currentKey) {
      // Actual update is necessary.
      Vue.set(
        cache.plannedFor[parentInfo.parentClass],
        "$firstActivePendingKey",
        parentInfo.parentKey
      );
    }
  }

  // Actually update the value.
  parentInfo.activePendingChildrenCount += delta;

  // If value zeroed in plannedFor relation, update $firstActivePendingKey.
  if (
    parentInfo.activePendingChildrenCount === 0 &&
    relation === "plannedFor" &&
    parentInfo.parentClass !== "list"
  ) {
    const currentKey =
      cache.plannedFor[parentInfo.parentClass].$firstActivePendingKey;
    if (parentInfo.parentKey === currentKey) {
      // Actual update is necessary.
      const classParentInfos = cache.plannedFor[parentInfo.parentClass];
      const candidates = Object.keys(classParentInfos);
      let bestCandidate = null;
      for (const candidate of candidates) {
        if (
          candidate !== "$firstActivePendingKey" &&
          classParentInfos[candidate] &&
          classParentInfos[candidate].activePendingChildrenCount &&
          (bestCandidate === null || candidate < bestCandidate)
        ) {
          bestCandidate = candidate;
        }
      }
      Vue.set(
        cache.plannedFor[parentInfo.parentClass],
        "$firstActivePendingKey",
        bestCandidate
      );
    }
  }
}

export function getSiblings(cache, relation, obj) {
  return getParentInfo(cache, relation, obj[relation]).children;
}

function migrateDataFormat(obj, cache, db) {
  // Don't modify malformed objects.
  if (!obj._id) {
    return;
  }
  if (obj._id.startsWith("list:")) {
    if (obj.subtaskOf) {
      delete obj.subtaskOf;
    }
    if (!obj.relation) {
      // make lists aware of the relation type they represent.
      obj.relation = "subtaskOf";
    }
    if (!obj.type) {
      obj.type = "list";
    }
    return;
  }
  if (!obj._id.startsWith("task:")) {
    return;
  }
  // Cleanup backlog-as-subtask experiments
  if (obj.subtaskOf && obj.subtaskOf.parentClass === "backlog") {
    if (!obj.plannedFor) {
      obj.plannedFor = obj.subtaskOf;
    }
    console.info("Item", obj._id, "is marked as a subtask of unplanned, fixing...");
    delete obj.subtaskOf;
  }
  if (
    obj.plannedFor &&
    ["backlog", "inbox", "notatki"].includes(obj.plannedFor.parentClass)
  ) {
    obj.plannedFor.parentClass = "list";
  }
  // Update for multilist
  if (obj.subtaskOf && obj.subtaskOf.parentClass === "list" && !obj.plannedFor) {
    let backlogByDefault = false
    for (const setting of Object.values(cache.metadata).map(db => db.settings.backlogByDefault)) {
      if (setting) backlogByDefault = true;
    }
    if (backlogByDefault) {
      obj.plannedFor = {
        parentKey: "backlog",
        parentClass: "list",
      };
    }
  }
  if (!obj.subtaskOf) {
    obj.subtaskOf = {
      parentKey: "default",
      parentClass: "list",
    };
  }
  if (obj.subtaskOf.parentKey === "default") {
    obj.subtaskOf.parentKey = defaultListID(obj.couch);
  }
  // set explicit type, drop old checkbox visibility property
  if (!obj.type) {
    if (obj.hideCheckbox) {
      obj.type = "note";
    } else {
      obj.type = "task";
    }
  }
  // uncomment after new types are deployed on all environments
  if (obj.hideCheckbox) {
    delete obj.hideCheckbox;
  }

  // make tasks aware of the relation type they represent.
  if (!obj.relation) {
    obj.relation = "subtaskOf";
  }
}

// Forward compatibility: read "backlog" items from all possible places
// they could be placed.
export function getBacklogItems(cache) {
  const result = [];
  for (const relation of ["plannedFor", "subtaskOf"]) {
    if (cache[relation]["backlog"]) {
      for (const entry of Object.values(cache[relation]["backlog"])) {
        result.push(...entry.children);
      }
    }
  }
  if (cache.plannedFor.list && cache.plannedFor.list.backlog) {
    result.push(...cache.plannedFor.list.backlog.children);
  }
  return result;
}

function processRecord(cache, db, record) {
  // This function may be fed data from different sources, with different structure:
  // db.changes: {last_seq, results: Array[{changes, doc?, deleted?, id, seq}]}
  // db.allDocs: {offset, rows: Array[{id, key, value, doc?, error?}], total_rows}
  // db.find: {docs: Array[doc]}
  if (record.error) {
    return;
  }
  // compatibility layer for handling these various formats
  const id = record._id ? record._id : record.id;
  const doc = record._id ? record : record.doc;
  if (doc === null) {
    console.error("empty doc:", id)
    return;
  }

  // Special documents with metadata don't have IDs unique across couches,
  // so they cannot be stored together like docs with items.
  const metadataIDs = ["settings", "statistics", ".db_info"];
  if (metadataIDs.includes(id)) {
    Vue.set(cache.metadata[db.name], id, new Item(doc, cache, db));

    if (id === ".db_info") {
      db.displayName = doc.displayName;
      db.primary = doc.primary;
    }
    return;
  }
  // Skip internal couch documents.
  if (id[0] === "_") {
    return;
  }
  if (id.startsWith("list:")) {
    cache.lists[id] = doc.name;
  }
  if (id.startsWith("layout:")) {
    cache.layouts[id] = doc.name;
  }
  if (id.startsWith("label:")) {
    cache.labels[id] = doc.name;
  }

  // We may use other conditions for removing objects from the cache,
  // not just the actual deletion.
  const consideredDeleted = record.deleted || doc.trashed;
  const oldObj = cache.docs[id] || {};
  const newObj = consideredDeleted ? {} : new Item(doc, cache, db);
  migrateDataFormat(newObj, cache, db);

  const wasActivePending = oldObj._id && activePending(oldObj);
  const isActivePending = !consideredDeleted && activePending(newObj);

  for (const relation of Object.keys(RELATIONS)) {
    const oldPlacement = oldObj[relation];
    const newPlacement = newObj[relation];
    const sameParent =
      oldPlacement && newPlacement &&
      newPlacement.parentClass === oldPlacement.parentClass &&
      newPlacement.parentKey === oldPlacement.parentKey;
    if (sameParent && newPlacement.seq === oldPlacement.seq) {
      // Replace the object in place.
      const oldParentInfo = getParentInfo(cache, relation, oldPlacement);
      const i = oldParentInfo.children.findIndex((c) => c._id === id);
      Vue.set(oldParentInfo.children, i, newObj);
      if (isActivePending !== wasActivePending) {
        modifyActivePendingCount(
          cache,
          relation,
          oldParentInfo,
          isActivePending ? 1 : -1
        );
      }
      continue;
    }
    if (oldPlacement) {
      // Remove from the old place.
      const oldParentInfo = getParentInfo(cache, relation, oldPlacement);
      removeFromParentList(oldParentInfo.children, relation, oldObj);
      if (wasActivePending && (!sameParent || !isActivePending)) {
        modifyActivePendingCount(cache, relation, oldParentInfo, -1);
      }
    }
    if (newPlacement) {
      // Insert into the new place.
      const newParentInfo = getOrCreateParentInfo(cache, relation, newPlacement);
      sortIntoParentList(newParentInfo.children, relation, newObj);
      if (isActivePending && (!sameParent || !wasActivePending)) {
        modifyActivePendingCount(cache, relation, newParentInfo, +1);
      }
    }
  }
  if (consideredDeleted) {
    Vue.delete(cache.docs, id);
    Vue.delete(cache.local, id);
    if (id.startsWith("list:")) {
      Vue.delete(cache.lists, id);
    }
    if (id.startsWith("layout:")) {
      Vue.delete(cache.layouts, id);
    }
    if (id.startsWith("label:")) {
      Vue.delete(cache.labels, id);
    }
  } else {
    Vue.set(cache.docs, id, newObj);
    if (!cache.local[id]) {
      // Attempt to load the local data for the item in case it is there
      // but wasn't loaded yet.
      db.get(newObj.localDataId).then(
        (localValue) => {
          Vue.set(cache.local, id, localValue);
        },
        () => { /* Ignore errors. */ },
      );
    }
  }
}

export async function initializeCache(dbs) {
  const cache = {
    docs: {},
    lists: {},
    layouts: {},
    labels: {},
    local: {},
    metadata: {},
  };
  for (const [relation, parentClasses] of Object.entries(RELATIONS)) {
    cache[relation] = {};
    for (const parentClass of parentClasses) {
      cache[relation][parentClass] = {};
    }
  }

  // Freeze promise allows freezeing cache update during bulk updates. This
  // way we avoid multiple updates hitting Vue's reactive machine separately,
  // and we can expect the result to show in one, maybe two updates, instead
  // of 1 per updated item.
  cache.freezePromise = Promise.resolve();
  Object.defineProperties(cache, {
    // Call reactiveCache.freezeUntil(somePromise) to help processing
    // bulk changes in a single batch, without causing multiple Vue updates
    // on each separate change.
    // Returns the original promise, so that it can be used as a transparent
    // wrapper.
    freezeUntil: {
      value(promiseToWaitFor) {
        const f = () => promiseToWaitFor;
        cache.freezePromise = cache.freezePromise.then(f, f);
        return promiseToWaitFor;
      },
    },
  });

  // Publish the Item class for the tests.
  cache.Item = Item;

  for (const db of dbs) {
    cache.metadata[db.name] = {};
    const emptySettings = new Item({_id: "settings"}, cache, db)
    cache.metadata[db.name].settings = emptySettings

    const dbMetadata = await db.allDocs({
      keys: [".db_info", "settings"],
      include_docs: true,
    });
    for (const row of dbMetadata.rows) {
      // The row format is similar enough to the change format that we can pass
      // it directly as a change. The only fields that we read are
      // `id`, `doc` (both properly set in the row), and `deleted` (unset in the
      // row - there are no rows for deleted docs).
      processRecord(cache, db, row);
    }

    if (!db.displayName) {
      console.info("Missing database displayName, falling back to " + db.name);
      db.displayName = db.name;
    }

    const listID = defaultListID(db);
    if (!cache.lists[listID]) {
      const defaultListName = dbs.length > 1
        ? db.displayName + " default"
        : "Items without a list";
      cache.docs[listID] = new Item(
        { _id: listID, name: defaultListName, type: "list", relation: "subtaskOf" },
        cache,
        db,
      );
      cache.lists[listID] = defaultListName;
    }
    const calViewLayout = "calendar-view-layout";
    const primaryDB = db.name.endsWith('_default');
    if (primaryDB && !cache.docs[calViewLayout]) {
      cache.docs[calViewLayout] = new Item(
        {
          _id: calViewLayout,
          name: "Calendar view layout",
          type: "layout",
          layout: ["days", "weeks", "backlog"],
        },
        cache,
        db,
      );
    }
  }

  if (!cache.docs.statistics) {
    cache.docs.statistics = {
      _id: "statistics",
    };
  }

  return cache;
}

export async function populateCache(cache, db, data) {
  for (const record of data) {
    // The row format is similar enough to the change format that we can pass
    // it directly as a change. The only fields that we read are
    // `id`, `doc` (both properly set in the row), and `deleted` (unset in the
    // row - there are no rows for deleted docs).
    processRecord(cache, db, record);
  }
  return cache;
}

function watchChanges(cache, dbConfigs) {
  for (const conf of dbConfigs) {
    let pendingChanges = null;
    function processPendingChanges() {
      performance.mark("start");
      function processChange(change) {
        processRecord(cache, conf.db, change);
      }
      pendingChanges.forEach(processChange);
      performance.mark("end");
      const measure = performance.measure("processPendingChanges()", "start", "end");
      console.debug(
        "processPendingChanges() with " + pendingChanges.length + " changes: " +
        measure.duration.toFixed(2) + "ms"
      );
      pendingChanges = null;
    }
    function handleChange(change) {
      if (pendingChanges) {
        pendingChanges.push(change);
      } else {
        pendingChanges = [change];
        // TODO: Consider waiting more if the view was re-frozen.
        cache.freezePromise.then(processPendingChanges, processPendingChanges);
      }
    }
    conf.db.changes({
      since: conf.update_seq,
      live: true,
      include_docs: true,
    }).on("change", handleChange);
  }
}

export async function createReactiveCache(dbs) {
  performance.mark("initCache-start");
  const cache = await initializeCache(dbs);
  performance.mark("initCache-stop");
  performance.measure("5.1. initializing cache", "initCache-start", "initCache-stop");

  const dbConfigs = [];
  for (const db of dbs) {
    performance.mark("begin");
    const data = await db.allDocs({ include_docs: true, update_seq: true });
    performance.mark("end");
    performance.measure("5.2. loading from pouch " + db.name, "begin", "end");
    dbConfigs.push({ db, update_seq: data.update_seq });
    performance.mark("begin");
    await populateCache(cache, db, data.rows);
    performance.mark("end");
    performance.measure("5.3. processing data from " + db.name, "begin", "end");
  }
  watchChanges(cache, dbConfigs);
  return cache;
}
